/**
 * Build greenfield filter sidebar ViewModel from existing V1 filter state + traits.
 * Presentation model only — mutations go through ResultsStepCard setFilter.
 * Does not change V1 filter engine / trait calculation.
 */

/** V1 time buckets — same granularity as FlightResultsFiltersPanel.TIME_OPTIONS */
export const GREENFIELD_TIME_BUCKETS = [
  { id: "morning", label: "בוקר", rangeLabel: "05–12" },
  { id: "afternoon", label: "צהריים", rangeLabel: "12–17" },
  { id: "evening", label: "ערב", rangeLabel: "17–21" },
  { id: "night", label: "לילה", rangeLabel: "21–05" },
];

/**
 * @param {{
 *   filterState: object,
 *   traitsList: object[],
 *   bounds?: { pricePerPerson?: [number, number], durationMin?: [number, number] }|null,
 *   airlinesCatalog?: Array<{ id?: string, code?: string, name?: string, Name?: string }>|null,
 * }} input
 */
export function buildGreenfieldFiltersViewModel(input = {}) {
  const filter = input.filterState || {};
  const traits = Array.isArray(input.traitsList) ? input.traitsList : [];
  const bounds = input.bounds || {};

  const stopCounts = countBy(traits, (t) => t.stopsBand);
  const stops = [
    {
      id: "direct",
      label: "ישיר",
      count: stopCounts.direct || 0,
      checked: filter.stops === "direct",
    },
    {
      id: "one",
      label: "עצירה אחת",
      count: stopCounts.one || 0,
      checked: filter.stops === "one",
    },
    {
      id: "two_plus",
      label: "2+ עצירות",
      count: stopCounts.two_plus || 0,
      checked: filter.stops === "two_plus",
    },
  ];

  const airlineCounts = new Map();
  for (const t of traits) {
    for (const code of t.airlines || []) {
      const c = String(code);
      airlineCounts.set(c, (airlineCounts.get(c) || 0) + 1);
    }
  }
  const nameMap = buildAirlineNameMap(input.airlinesCatalog);
  const selectedAirlines = new Set(
    Array.isArray(filter.airlines) ? filter.airlines.map(String) : []
  );
  const airlines = [...airlineCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([id, count]) => ({
      id,
      label: nameMap.get(id) || id,
      heLabel: nameMap.get(id) || id,
      count,
      checked: selectedAirlines.has(id),
    }));

  // Traits only prove option presence (hasTrolley/hasChecked), not included vs paid.
  // Never label as "כלול" without included evidence — cards use BaggageViewModel for that.
  const bagCounts = {
    trolley: traits.filter((t) => t.hasTrolley).length,
    checked: traits.filter((t) => t.hasChecked).length,
    personal_only: traits.filter((t) => !t.hasTrolley && !t.hasChecked).length,
  };
  const baggage = [
    {
      id: "trolley",
      label: "טרולי",
      count: bagCounts.trolley,
      checked: filter.baggage === "trolley",
    },
    {
      id: "checked",
      label: "מזוודה",
      count: bagCounts.checked,
      checked: filter.baggage === "checked",
    },
    {
      id: "personal_only",
      label: "ללא מזוודה",
      count: bagCounts.personal_only,
      checked: filter.baggage === "personal_only",
    },
  ];

  const priceBounds = bounds.pricePerPerson || [0, 0];
  const priceRange = filter.pricePerPersonRange;
  const priceMax =
    Array.isArray(priceRange) && priceRange.length === 2
      ? priceRange[1]
      : priceBounds[1];

  const durationBounds = bounds.durationMin || [0, 0];
  const durationRange = filter.durationRangeMin;
  const durationMax =
    Array.isArray(durationRange) && durationRange.length === 2
      ? durationRange[1]
      : durationBounds[1];

  return {
    stops,
    airlines,
    baggage,
    resultCount: traits.length,
    price: {
      min: priceBounds[0] ?? 0,
      max: priceBounds[1] ?? 0,
      value: priceMax ?? priceBounds[1] ?? 0,
    },
    duration: {
      min: durationBounds[0] ?? 0,
      maxMinutes: durationBounds[1] ?? 0,
      valueMinutes: durationMax ?? durationBounds[1] ?? 0,
    },
    outboundTime: {
      selected: filter.timeOutbound || null,
      options: GREENFIELD_TIME_BUCKETS.map((b) => ({
        ...b,
        checked: filter.timeOutbound === b.id,
      })),
    },
    returnTime: {
      selected: filter.timeReturn || null,
      options: GREENFIELD_TIME_BUCKETS.map((b) => ({
        ...b,
        checked: filter.timeReturn === b.id,
      })),
    },
  };
}

/**
 * Map greenfield filter UI actions → V1 setFilter patches.
 */
export function greenfieldFilterActionToPatch(action, currentFilter, bounds) {
  const filter = currentFilter || {};
  switch (action?.type) {
    case "clear":
      return { __replace: true };
    case "toggleStop": {
      const id = action.id;
      const next = filter.stops === id ? null : id;
      return { stops: next };
    }
    case "toggleAirline": {
      const id = String(action.id);
      const prev = Array.isArray(filter.airlines) ? [...filter.airlines] : [];
      const idx = prev.indexOf(id);
      if (idx >= 0) prev.splice(idx, 1);
      else prev.push(id);
      return { airlines: prev };
    }
    case "toggleBaggage": {
      const id = action.id;
      const next = filter.baggage === id ? null : id;
      return { baggage: next };
    }
    case "priceMax": {
      const lo = bounds?.pricePerPerson?.[0] ?? 0;
      const hi = Number(action.value);
      if (!Number.isFinite(hi)) return null;
      const maxBound = bounds?.pricePerPerson?.[1];
      if (maxBound != null && hi >= maxBound) {
        return { pricePerPersonRange: null };
      }
      return { pricePerPersonRange: [lo, hi] };
    }
    case "durationMax": {
      const lo = bounds?.durationMin?.[0] ?? 0;
      const hi = Number(action.value);
      if (!Number.isFinite(hi)) return null;
      const maxBound = bounds?.durationMin?.[1];
      if (maxBound != null && hi >= maxBound) {
        return { durationRangeMin: null };
      }
      return { durationRangeMin: [lo, hi] };
    }
    case "toggleOutboundTime": {
      const id = normalizeTimeBucketId(action.id);
      if (!id) return null;
      const next = filter.timeOutbound === id ? null : id;
      return { timeOutbound: next };
    }
    case "toggleReturnTime": {
      const id = normalizeTimeBucketId(action.id);
      if (!id) return null;
      const next = filter.timeReturn === id ? null : id;
      return { timeReturn: next };
    }
    // Legacy continuous-slider actions → still map to discrete V1 buckets only
    case "outboundTime": {
      return { timeOutbound: rangeToTimeBucket(action.min, action.max) };
    }
    case "returnTime": {
      return { timeReturn: rangeToTimeBucket(action.min, action.max) };
    }
    default:
      return null;
  }
}

function normalizeTimeBucketId(raw) {
  const id = String(raw || "").trim();
  if (GREENFIELD_TIME_BUCKETS.some((b) => b.id === id)) return id;
  return null;
}

function rangeToTimeBucket(minH, maxH) {
  const lo = Number(minH);
  const hi = Number(maxH);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (lo <= 0 && hi >= 24) return null;
  const mid = (lo + hi) / 2;
  if (mid >= 5 && mid < 12) return "morning";
  if (mid >= 12 && mid < 17) return "afternoon";
  if (mid >= 17 && mid < 21) return "evening";
  return "night";
}

function countBy(list, fn) {
  const out = {};
  for (const item of list) {
    const k = fn(item);
    if (k == null) continue;
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

function buildAirlineNameMap(catalog) {
  const m = new Map();
  for (const row of catalog || []) {
    const code = String(row?.id || row?.code || row?.IATA || "").trim();
    const name = String(row?.name || row?.Name || row?.heName || "").trim();
    if (code) m.set(code, name || code);
  }
  return m;
}
